/**
 * Authentication store
 * Manages user authentication state and tokens
 * Note: This is a basic implementation - enhance as needed for production
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * User interface
 */
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user';
  createdAt: string;
}

/**
 * Auth tokens
 */
interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

/**
 * Auth state interface
 */
interface AuthState {
  // State
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
  updateUser: (updates: Partial<User>) => void;
  isTokenExpired: () => boolean;
  getAccessToken: () => string | null;
}

/**
 * Storage key for auth
 */
const AUTH_STORAGE_KEY = 'maps-scraper-auth';

/**
 * Auth store with persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      setTokens: (tokens) => {
        set({ tokens });
      },

      login: (user, tokens) => {
        set({
          user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
        });

        // Clear any other auth-related storage
        localStorage.removeItem(AUTH_STORAGE_KEY);
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } });
        }
      },

      isTokenExpired: () => {
        const tokens = get().tokens;
        if (!tokens) return true;

        // Consider expired if within 60 seconds of expiration
        return Date.now() >= tokens.expiresAt - 60000;
      },

      getAccessToken: () => {
        const state = get();

        if (!state.tokens) return null;
        if (state.isTokenExpired()) return null;

        return state.tokens.accessToken;
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Check token validity on rehydration
        if (state) {
          state.isLoading = false;

          // If token is expired, clear auth state
          if (state.tokens && Date.now() >= state.tokens.expiresAt) {
            state.user = null;
            state.tokens = null;
            state.isAuthenticated = false;
          }
        }
      },
    }
  )
);

/**
 * Hook for auth actions
 */
export function useAuth() {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const updateUser = useAuthStore((state) => state.updateUser);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser,
    isAdmin: user?.role === 'admin',
  };
}

/**
 * Hook to get access token for API calls
 */
export function useAccessToken() {
  const getAccessToken = useAuthStore((state) => state.getAccessToken);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);

  return {
    getToken: getAccessToken,
    isExpired: isTokenExpired,
  };
}

/**
 * Check if user has required role
 */
export function useHasRole(requiredRole: 'admin' | 'user') {
  const user = useAuthStore((state) => state.user);

  if (!user) return false;
  if (requiredRole === 'user') return true;
  return user.role === 'admin';
}

/**
 * Selector for authenticated state (for conditional rendering)
 */
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectUser = (state: AuthState) => state.user;
export const selectIsLoading = (state: AuthState) => state.isLoading;
